import {Component, HostListener, OnDestroy, OnInit} from '@angular/core';
import {AuthService} from '../../../services/auth/auth.service';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {EMPTY, merge, Subject, timer} from 'rxjs';
import {catchError, debounceTime, distinctUntilChanged, filter, switchMap, takeUntil, throttleTime} from 'rxjs/operators';
import {UserNotificationService} from '../../../services/notification/user-notification.service';
import {FolderService} from '../../../services/folder/folder.service';
import {CurrentUserService} from '../../../services/user/current-user.service';


interface HeaderItem {
  name: string;
  route: string;
}

const SUBTRACT_WIDTH = 500;
const BADGE_POLL_INTERVAL = 60000;

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    standalone: false
})
export class HeaderComponent implements OnInit, OnDestroy {
  standaloneItems: HeaderItem[] = [
    {name: 'Wortschatz', route: 'wortschatz'},
    {name: 'Kannbeschreibungen', route: 'kannbeschreibungen'},
  ];
  dropdownItems: HeaderItem[] = [
    {name: 'Grammatik', route: 'grammatik'},
    {name: 'Textsorten', route: 'textsorten'},
    {name: 'Textmuster', route: 'textmuster'},
    {name: 'Sprachhandlungen', route: 'sprachhandlungen'}
  ];
  screenWidth: number;
  screenSub$ = new Subject<number>();
  searchExpanded = false;
  unreadCount = 0;
  pendingFolderCount = 0;

  private destroy$ = new Subject<void>();

  constructor(public authService: AuthService, private router: Router, private route: ActivatedRoute,
              private userNotificationService: UserNotificationService, private folderService: FolderService,
              private currentUserService: CurrentUserService) {
  }

  /** the number fits the small dot up to 9, more are shown as "+" */
  get unreadLabel(): string {
    return this.unreadCount > 9 ? '+' : String(this.unreadCount);
  }

  canUseFolders(): boolean {
    return this.currentUserService.canUseFolders();
  }

  @HostListener('window:resize', ['$event'])
  setScreenSize() {
    this.screenWidth = window.innerWidth;
    this.screenSub$.next(this.screenWidth);
    this.moveHeaderItemsDynamically(this.screenWidth);
  }

  ngOnInit() {
    this.setScreenSize();
    this.screenSub$.pipe(
      debounceTime(800),
      distinctUntilChanged()
    ).subscribe((screenWidth: number) => {
      this.moveHeaderItemsDynamically(screenWidth);
    });
    this.userNotificationService.unreadCount$.pipe(takeUntil(this.destroy$))
      .subscribe(count => this.unreadCount = count);
    this.folderService.pendingCount$.pipe(takeUntil(this.destroy$))
      .subscribe(count => this.pendingFolderCount = count);
    this.pollBadges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * refreshes the unread notifications and pending Sammelmappen regularly and after navigating
   * (e.g. after the login), the services publish the counts
   */
  private pollBadges(): void {
    const navigation$ = this.router.events.pipe(filter(event => event instanceof NavigationEnd));
    merge(timer(0, BADGE_POLL_INTERVAL), navigation$).pipe(
      throttleTime(5000, undefined, {leading: true, trailing: true}),
      filter(() => this.authService.isLoggedIn()),
      switchMap(() => merge(
        this.userNotificationService.refreshUnreadCount(),
        this.canUseFolders() ? this.folderService.getPendingFolders({size: 1}) : EMPTY
      ).pipe(catchError(() => EMPTY))),
      takeUntil(this.destroy$)
    ).subscribe();
  }

  logoutAndReloadPage() { // TODO delete if not necessary (maybe for logging out blocked user)
    this.authService.logoutUser();
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.router.onSameUrlNavigation = 'reload';
    this.router.navigate(['./'], {relativeTo: this.route});
  }

  moveHeaderItemsDynamically(screenWidth): void {
    let subtractWidth;
    if (this.authService.getUser() !== undefined) {
      subtractWidth = SUBTRACT_WIDTH + this.authService.getUser().length * 10;
    } else {
      subtractWidth = SUBTRACT_WIDTH;
    }
    if ((screenWidth - subtractWidth) / (this.standaloneItems.length + 1) > 210 && this.dropdownItems.length > 0) { // reduce dropdown items
      let itemToMove = this.dropdownItems.shift();
      this.standaloneItems.push(itemToMove);
      if (this.dropdownItems.length === 1) {
        itemToMove = this.dropdownItems.shift();
        this.standaloneItems.push(itemToMove);
      } else {
        this.moveHeaderItemsDynamically(screenWidth);
      }
    } else if ((screenWidth - subtractWidth) / (this.standaloneItems.length) < 210 && this.standaloneItems.length > 0) { // reduce standalone items
      const itemToMove = this.standaloneItems.pop();
      this.dropdownItems.unshift(itemToMove);
      this.moveHeaderItemsDynamically(screenWidth);
    }
  }
}
